/**
 * Registered Pharmacy Patients – Types, constants, API helpers & fetch functions.
 *
 * Extracted from `invoiceHelper.tsx` so that the pharmacy-patient
 * concerns live in a dedicated, easy-to-maintain module.
 *
 * API endpoint: /api/pharmacy-invoice/patients/
 */

import { getRequest } from '../../../services/httpService';

// ─── Enums & Constants ───────────────────────────────────────────────────────

/**
 * Registration type returned by the `/api/pharmacy-invoice/patients/` API.
 *
 * - **Pharmacy** – walk-in patient without MRN
 * - **Registered** – patient registered with a full MRN
 */
export enum RegistrationTypeEnum {
  Pharmacy = 'Pharmacy',
  Registered = 'Registered',
}

/** Allows `null` for API responses where the field may be absent */
export type RegisteredPharmacyPatientType = 'Pharmacy' | 'Registered' | null;

/**
 * Visual style presets for each registration type badge.
 * Keyed by `RegistrationTypeEnum` so unknown values are handled gracefully.
 */
export const REGISTRATION_TYPE_COLORS: Record<
  RegistrationTypeEnum,
  { color: string; background: string; border: string }
> = {
  [RegistrationTypeEnum.Pharmacy]: {
    color: '#E87040',
    background: '#FFF0E8',
    border: '#F5C5A8',
  },
  [RegistrationTypeEnum.Registered]: {
    color: '#1A8CDB',
    background: '#E8F4FD',
    border: '#A8D4F0',
  },
};

// ─── Pagination meta (shared shape with clinical patients) ───────────────────

export interface PaginationMeta {
  limit: number;
  next: string | null;
  offset: number;
  previous: string | null;
  total_count: number;
}

// ─── Patient model ───────────────────────────────────────────────────────────

/** Single record returned by `/api/pharmacy-invoice/patients/` */
export interface RegisteredPharmacyPatient {
  id: number;
  first_name: string;
  last_name: string;
  date_of_birth: string | null;
  gender: number | null;
  mobile_number: string;
  mobile_number_country_code: string;
  email: string | null;
  patient_source: string | null;
  medical_record_no: string;
  registration_type: RegisteredPharmacyPatientType;
  created_on: string;
  clinic_id: number;
  patient_name: string;
  age: number | null;
  gender_name: string | null;
  full_mobile_number: string;
  clinic_name: string;
  is_new: boolean;
}

// ─── API response & filters ──────────────────────────────────────────────────

export interface RegisteredPharmacyPatientsResponse {
  meta: PaginationMeta;
  objects: RegisteredPharmacyPatient[];
}

export interface RegisteredPharmacyPatientsFilters {
  clinic: number;
  limit?: number;
  offset?: number;
  search?: string;
  registration_type?: 'Pharmacy' | 'Registered';
  is_new?: boolean;
}

// ─── Internal helpers ────────────────────────────────────────────────────────

/** Converts the filter object into URL search params for the API call */
function filtersToParams(filters: RegisteredPharmacyPatientsFilters): URLSearchParams {
  const { clinic, limit = 10, offset = 0, search, registration_type, is_new } = filters;
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
    clinic: String(clinic),
  });
  if (search) params.set('search', search);
  if (registration_type) params.set('registration_type', registration_type);
  if (is_new !== undefined) params.set('is_new', String(is_new));
  return params;
}

// ─── Fetch function ──────────────────────────────────────────────────────────

/**
 * Fetches registered pharmacy patients for invoice creation.
 *
 * @param filters — clinic, pagination & search criteria
 * @returns paginated list of `RegisteredPharmacyPatient`
 *
 * @example
 * ```ts
 * const { objects, meta } = await fetchRegisteredPharmacyPatients({
 *   clinic: 42,
 *   limit: 20,
 *   offset: 0,
 *   search: 'John',
 * });
 * ```
 */
export function fetchRegisteredPharmacyPatients(
  filters: RegisteredPharmacyPatientsFilters
): Promise<RegisteredPharmacyPatientsResponse> {
  const params = filtersToParams(filters);
  return getRequest(`pharmacy-invoice/patients/?${params.toString()}`);
}
